import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash2, CheckCircle, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import Modal from '../components/Modal';
import { Input, Select } from '../components/Form';

type Tab = 'ar' | 'ap';

interface Conta {
  id: string;
  description: string;
  amount: string | number;
  paidAmount?: string | number;
  dueDate: string;
  status: string;
  observations?: string | null;
  person?: { name?: string } | null;
}

const money = (v: any) =>
  'R$ ' + (parseFloat(v as string) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
const dateBR = (d?: string) => (d ? new Date(d).toLocaleDateString('pt-BR') : '-');

const METHODS = [
  { value: 'PIX', label: 'PIX' },
  { value: 'BANK_TRANSFER', label: 'Depósito/Transferência' },
  { value: 'CASH', label: 'Dinheiro' },
  { value: 'CHECK', label: 'Cheque' },
  { value: 'CREDIT_CARD', label: 'Cartão de crédito' },
  { value: 'DEBIT_CARD', label: 'Cartão de débito' },
];

export default function ContasPage() {
  const [tab, setTab] = useState<Tab>('ar');
  const [itens, setItens] = useState<Conta[]>([]);
  const [loading, setLoading] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ description: '', amount: '', dueDate: '', observations: '' });
  const [saving, setSaving] = useState(false);

  const [settleTarget, setSettleTarget] = useState<Conta | null>(null);
  const [settleForm, setSettleForm] = useState({ method: 'PIX', amount: '', pin: '' });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/finance/${tab}`);
      setItens(data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function create() {
    if (!createForm.description.trim() || !createForm.amount || !createForm.dueDate) {
      alert('Preencha descrição, valor e vencimento.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/finance/${tab}`, {
        description: createForm.description.trim(),
        amount: parseFloat(createForm.amount),
        dueDate: createForm.dueDate,
        observations: createForm.observations || undefined,
      });
      setShowCreate(false);
      setCreateForm({ description: '', amount: '', dueDate: '', observations: '' });
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao criar.');
    } finally {
      setSaving(false);
    }
  }

  function openSettle(c: Conta) {
    setSettleTarget(c);
    setSettleForm({ method: 'PIX', amount: String(c.amount ?? ''), pin: '' });
  }

  async function settle(event: React.FormEvent) {
    event.preventDefault();
    if (!settleTarget) return;
    if (!/^\d{6}$/.test(settleForm.pin)) {
      alert('Informe um PIN válido de 6 dígitos.');
      return;
    }
    try {
      await api.post(`/finance/${tab}/${settleTarget.id}/settle`, {
        method: settleForm.method,
        amount: parseFloat(settleForm.amount),
        pin: settleForm.pin,
      });
      setSettleTarget(null);
      await load();
      alert(tab === 'ar' ? 'Recebimento registrado.' : 'Pagamento registrado.');
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao dar baixa.');
    }
  }

  async function remove(id: string) {
    if (!confirm('Remover este lançamento?')) return;
    await api.delete(`/finance/${tab}/${id}`);
    load();
  }

  const isOverdue = (c: Conta) => c.status !== 'PAID' && new Date(c.dueDate) < new Date();
  const totalPend = itens
    .filter((c) => c.status !== 'PAID')
    .reduce((a, c) => a + (parseFloat(c.amount as string) || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold dark:text-dark-text-primary">Contas a Pagar / Receber</h1>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg">
          <Plus className="w-4 h-4" /> Nova conta {tab === 'ar' ? 'a receber' : 'a pagar'}
        </button>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setTab('ar')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'ar' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-dark-bg-tertiary dark:text-dark-text-secondary'}`}
        >
          <ArrowDownCircle className="w-4 h-4" /> A Receber
        </button>
        <button
          onClick={() => setTab('ap')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'ap' ? 'bg-red-600 text-white' : 'bg-gray-100 dark:bg-dark-bg-tertiary dark:text-dark-text-secondary'}`}
        >
          <ArrowUpCircle className="w-4 h-4" /> A Pagar
        </button>
        <div className="ml-auto text-sm text-gray-500 dark:text-dark-text-secondary self-center">
          Pendente: <strong className={tab === 'ar' ? 'text-emerald-600' : 'text-red-600'}>{money(totalPend)}</strong>
        </div>
      </div>

      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-dark-bg-tertiary text-left text-gray-500 dark:text-dark-text-secondary">
            <tr>
              <th className="px-4 py-2">Descrição</th>
              <th className="px-4 py-2">{tab === 'ar' ? 'Cliente' : 'Fornecedor'}</th>
              <th className="px-4 py-2">Vencimento</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Valor</th>
              <th className="px-4 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Carregando...</td></tr>}
            {!loading && itens.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Nenhum lançamento.</td></tr>}
            {itens.map((c) => (
              <tr key={c.id} className="border-t border-gray-100 dark:border-dark-border dark:text-dark-text-primary">
                <td className="px-4 py-2">{c.description}</td>
                <td className="px-4 py-2 text-gray-500 dark:text-dark-text-secondary">{c.person?.name || '—'}</td>
                <td className="px-4 py-2">{dateBR(c.dueDate)}</td>
                <td className="px-4 py-2">
                  {c.status === 'PAID' ? (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">Pago</span>
                  ) : isOverdue(c) ? (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">Vencido</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">Pendente</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-medium">{money(c.amount)}</td>
                <td className="px-4 py-2 text-right space-x-1">
                  {c.status !== 'PAID' && (
                    <button onClick={() => openSettle(c)} className="p-1 text-emerald-600" title="Dar baixa">
                      <CheckCircle className="w-4 h-4 inline" />
                    </button>
                  )}
                  <button onClick={() => remove(c.id)} className="p-1 text-red-600" title="Remover">
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <Modal isOpen onClose={() => setShowCreate(false)} title={`Nova conta ${tab === 'ar' ? 'a receber' : 'a pagar'}`} size="md">
          <div className="space-y-4">
            <Input label="Descrição" value={createForm.description} onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))} />
            <div className="grid grid-cols-2 gap-4">
              <Input label="Valor (R$)" type="number" step="0.01" value={createForm.amount} onChange={(e) => setCreateForm((p) => ({ ...p, amount: e.target.value }))} />
              <Input label="Vencimento" type="date" value={createForm.dueDate} onChange={(e) => setCreateForm((p) => ({ ...p, dueDate: e.target.value }))} />
            </div>
            <Input label="Observações (opcional)" value={createForm.observations} onChange={(e) => setCreateForm((p) => ({ ...p, observations: e.target.value }))} />
            <div className="flex justify-end gap-3 pt-2 border-t">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button onClick={create} disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50">{saving ? 'Salvando...' : 'Criar'}</button>
            </div>
          </div>
        </Modal>
      )}

      {settleTarget && (
        <Modal isOpen onClose={() => setSettleTarget(null)} title={tab === 'ar' ? 'Registrar recebimento' : 'Registrar pagamento'} size="md">
          <form onSubmit={settle} className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-dark-text-secondary">{settleTarget.description}</p>
            <Select label="Forma" value={settleForm.method} onChange={(e) => setSettleForm((p) => ({ ...p, method: e.target.value }))} options={METHODS} />
            <Input label="Valor (R$)" type="number" step="0.01" value={settleForm.amount} onChange={(e) => setSettleForm((p) => ({ ...p, amount: e.target.value }))} required />
            <Input label="PIN financeiro (6 dígitos)" type="password" inputMode="numeric" autoComplete="off" value={settleForm.pin} onChange={(e) => setSettleForm((p) => ({ ...p, pin: e.target.value }))} required />
            <div className="flex justify-end gap-3 pt-2 border-t">
              <button type="button" onClick={() => setSettleTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Voltar</button>
              <button type="submit" className="btn-primary">Confirmar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
